import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import queryString from 'query-string';
import { LazyOutlet } from 'design-system/atoms/spinner-suspense';
import { useContextOrg } from '../ContextOrg';
import { TimePeriod } from '../../../types/timePeriod';
import {
  NightDayPeriod,
  PlanConstraints,
  PlanInsightType,
  PlanSettingsParams,
  PredictionMode,
} from '../../../types/plan';

export interface SiteControlsState {
  period: TimePeriod;
  hiddenRoutes: string[];
  hiddenWorkAreas: string[];
  hiddenVms: string[];
  planInsightType: PlanInsightType;
  planSettings: PlanSettingsParams;
  planConstraints: PlanConstraints;
  publicTransport: {
    hiddenRoutes: string[];
  };
}
export interface SiteContextType {
  orgId: string;
  siteId: string;
  controls: SiteControlsState;
  setControls: (controls: Partial<SiteControlsState>) => void;
  getPresetControlsUrl: (controls: Partial<SiteControlsState>) => string;
}

export const emptyControlsState: SiteControlsState = {
  period: {
    type: 'TIME_PERIOD_LAST_24_HOURS',
  },
  hiddenRoutes: [],
  hiddenWorkAreas: [],
  hiddenVms: [],
  planInsightType: 'delay',
  planSettings: {
    daysOfTheWeek: [0, 1, 2, 3, 4, 5, 6],
    period: NightDayPeriod.DAY,
    mode: PredictionMode.AVERAGE,
  },
  planConstraints: {
    startTime: '',
    endTime: '',
    kpi: undefined,
    showConstraints: false,
  },
  publicTransport: {
    hiddenRoutes: [],
  },
};

export const SiteContext = createContext<SiteContextType | null>(null);

export function useContextSite(): SiteContextType {
  const context = useContext(SiteContext);
  if (context === null) {
    throw new Error('Trying to use SiteContext outside of its provider');
  }
  return context;
}

export function useUpdateSiteControlsURL<
  TFields extends Array<keyof SiteContextType['controls']>,
>(fields: TFields) {
  const { controls } = useContextSite();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = queryString.parse(location.search, {
    types: { controls: 'string' },
  });
  const urlControls = JSON.parse(
    typeof searchParams.controls === 'string' ? searchParams.controls : '{}'
  );
  useEffect(() => {
    if (
      fields.some(
        (field) =>
          JSON.stringify(controls[field]) !== JSON.stringify(urlControls[field])
      )
    ) {
      navigate(
        {
          ...location,
          search: queryString.stringify({
            ...searchParams,
            controls: JSON.stringify(
              fields.reduce(
                (accControls, field) => ({
                  ...accControls,
                  [field]: controls[field],
                }),
                urlControls
              )
            ),
          }),
        },
        {
          replace: true,
        }
      );
    }
  }, [fields, controls, urlControls, location, searchParams, navigate]);
}

export default function ContextSite() {
  const { siteId } = useParams();
  if (!siteId) {
    throw new Error('siteId is required');
  }
  const { orgId } = useContextOrg();
  const location = useLocation();
  const searchParams = queryString.parse(location.search, {
    types: { controls: 'string' },
  });
  const urlControls = JSON.parse(
    typeof searchParams.controls === 'string' ? searchParams.controls : '{}'
  );
  const [controls, setControls] = useState({
    ...emptyControlsState,
    ...urlControls,
  });
  const contextValue = useMemo(
    () => ({
      orgId,
      siteId,
      controls,
      setControls: (partialValue: Partial<SiteControlsState>) => {
        setControls((v) => ({ ...v, ...partialValue }));
      },
      getPresetControlsUrl: (controlsValue: Partial<SiteControlsState>) =>
        queryString.stringifyUrl({
          url: window.location.href,
          query: {
            controls: JSON.stringify(controlsValue),
          },
        }),
    }),
    [siteId, orgId, controls, setControls]
  );
  return (
    <SiteContext.Provider value={contextValue}>
      <LazyOutlet />
    </SiteContext.Provider>
  );
}
